import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCwIcon, PlusIcon, PlayIcon } from "lucide-react";
import { usePermissions } from "../../PermissionsProvider";
import {
  BaseModelCandidateService,
  BaseModelCandidate,
} from "../../services/BaseModelCandidateService";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";

const STATUS_VARIANT: Record<string, string> = {
  PENDING: "bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20",
  DISCOVERING:
    "bg-blue-500/15 text-blue-600 dark:text-blue-400 border-blue-500/20",
  CONFIG_READY:
    "bg-amber-500/15 text-amber-600 dark:text-amber-400 border-amber-500/20",
  READY: "bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20",
  FAILED: "bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20",
};

const BaseModelCandidatesOverview: React.FC = () => {
  const { apiKey } = usePermissions();
  const [candidates, setCandidates] = useState<BaseModelCandidate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addHfModelId, setAddHfModelId] = useState("");
  const [addDisplayName, setAddDisplayName] = useState("");
  const [addSkipDiscovery, setAddSkipDiscovery] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [discoveringId, setDiscoveringId] = useState<number | null>(null);

  const fetchCandidates = useCallback(() => {
    BaseModelCandidateService.listCandidates(setIsLoading, apiKey).subscribe(
      (data: BaseModelCandidate[]) => setCandidates(data),
      (error: unknown) => {
        console.error("Failed to load candidates:", error);
        toast.error("Failed to load candidates");
      }
    );
  }, [apiKey]);

  useEffect(() => {
    fetchCandidates();
  }, [fetchCandidates]);

  const handleAdd = () => {
    const hfId = addHfModelId.trim();
    if (!hfId) {
      toast.error("HuggingFace model ID is required");
      return;
    }
    setAddSubmitting(true);
    BaseModelCandidateService.createCandidate(
      hfId,
      addDisplayName.trim() || null,
      addSkipDiscovery,
      null,
      apiKey
    ).subscribe(
      () => {
        toast.success("Candidate added");
        setAddDialogOpen(false);
        setAddHfModelId("");
        setAddDisplayName("");
        setAddSkipDiscovery(false);
        fetchCandidates();
      },
      (error: unknown) => {
        toast.error(typeof error === "string" ? error : "Failed to add candidate");
      }
    ).add(() => setAddSubmitting(false));
  };

  const handleDiscover = (c: BaseModelCandidate) => {
    if (c.status !== "PENDING" && c.status !== "FAILED") return;
    setDiscoveringId(c.id);
    BaseModelCandidateService.discover(c.id, null, apiKey).subscribe(
      () => {
        toast.success(`Discovery complete for ${c.hf_model_id}`);
        fetchCandidates();
      },
      (error: unknown) => {
        toast.error(
          typeof error === "string" ? error : "Discovery failed"
        );
      }
    ).add(() => setDiscoveringId(null));
  };

  const readyCount = candidates.filter((c) => c.status === "READY").length;
  const failedCount = candidates.filter((c) => c.status === "FAILED").length;
  const pendingCount = candidates.filter((c) => c.status === "PENDING").length;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-2xl font-gc-demi-bold text-foreground">
          Base Model Candidates
        </h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={fetchCandidates}
            disabled={isLoading}
          >
            <RefreshCwIcon
              className={`w-4 h-4 mr-1.5 ${isLoading ? "animate-spin" : ""}`}
            />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setAddDialogOpen(true)}>
            <PlusIcon className="w-4 h-4 mr-1.5" />
            Add Candidate
          </Button>
        </div>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Zero-day model support: add HuggingFace models for finetuning. Discovery
        runs by default and auto-promotes on success.
      </p>

      <div className="flex gap-2 mb-4">
        <Badge variant="secondary">{candidates.length} total</Badge>
        <Badge className="bg-green-500/15 text-green-600 dark:text-green-400 border-green-500/20">
          {readyCount} ready
        </Badge>
        {pendingCount > 0 && (
          <Badge className="bg-yellow-500/15 text-yellow-600 dark:text-yellow-400 border-yellow-500/20">
            {pendingCount} pending
          </Badge>
        )}
        {failedCount > 0 && (
          <Badge className="bg-red-500/15 text-red-600 dark:text-red-400 border-red-500/20">
            {failedCount} failed
          </Badge>
        )}
      </div>

      {isLoading && candidates.length === 0 ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <RefreshCwIcon className="w-5 h-5 animate-spin mr-2" />
          Loading...
        </div>
      ) : candidates.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          No candidates. Add one to get started.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">ID</TableHead>
              <TableHead>HF Model ID</TableHead>
              <TableHead>Display Name</TableHead>
              <TableHead className="w-28">Status</TableHead>
              <TableHead className="w-28">Family</TableHead>
              <TableHead className="w-36">Created</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {candidates.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-mono text-xs">{c.id}</TableCell>
                <TableCell className="font-mono text-sm">
                  {c.hf_model_id}
                </TableCell>
                <TableCell className="text-sm">
                  {c.display_name || "—"}
                </TableCell>
                <TableCell>
                  <span
                    className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold ${
                      STATUS_VARIANT[c.status] || "bg-muted text-muted-foreground"
                    }`}
                  >
                    {c.status}
                  </span>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {c.inferred_family || "—"}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                  {c.date_created
                    ? new Date(c.date_created).toLocaleString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                        hour: "numeric",
                        minute: "2-digit",
                      })
                    : "—"}
                </TableCell>
                <TableCell>
                  {(c.status === "PENDING" || c.status === "FAILED") && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDiscover(c)}
                      disabled={discoveringId === c.id}
                    >
                      <PlayIcon
                        className={`w-4 h-4 mr-1 ${
                          discoveringId === c.id ? "animate-spin" : ""
                        }`}
                      />
                      {discoveringId === c.id ? "Discovering..." : "Discover"}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Base Model Candidate</DialogTitle>
            <DialogDescription>
              Add a HuggingFace model for the zero-day pipeline. The model must
              exist on HuggingFace. Discovery runs by default.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="hf-model-id">HuggingFace Model ID *</Label>
              <Input
                id="hf-model-id"
                placeholder="Qwen/Qwen2.5-27B-Instruct"
                value={addHfModelId}
                onChange={(e) => setAddHfModelId(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="display-name">Display Name (optional)</Label>
              <Input
                id="display-name"
                placeholder="Derived from model ID if empty"
                value={addDisplayName}
                onChange={(e) => setAddDisplayName(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="skip-discovery"
                checked={addSkipDiscovery}
                onChange={(e) => setAddSkipDiscovery(e.target.checked)}
                className="rounded border-border"
              />
              <Label htmlFor="skip-discovery">Skip discovery (add only)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAddDialogOpen(false)}
              disabled={addSubmitting}
            >
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={addSubmitting}>
              {addSubmitting ? "Adding..." : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BaseModelCandidatesOverview;
